import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../../../config/supabase';

// Each category maps to the Supabase table(s) it clears. Everything is scoped to
// the current agency, so one agency can NEVER wipe another's data.
const CATEGORIES = [
  { id: 'cars',          tables: ['cars'],                          fr: 'Voitures',          ar: 'السيارات',        emoji: '🚗' },
  { id: 'clients',       tables: ['clients'],                       fr: 'Clients',           ar: 'العملاء',         emoji: '👥' },
  { id: 'rentals',       tables: ['rentals'],                       fr: 'Contrats',          ar: 'العقود',          emoji: '📄' },
  { id: 'reservations',  tables: ['reservations'],                  fr: 'Réservations',      ar: 'الحجوزات',        emoji: '📆' },
  { id: 'vidanges',      tables: ['vidanges'],                      fr: 'Vidanges',          ar: 'الصيانة',         emoji: '🔧' },
  { id: 'cheques',       tables: ['cheques'],                       fr: 'Chèques',           ar: 'الشيكات',         emoji: '💳' },
  { id: 'staff',         tables: ['staff', 'staff_payments'],       fr: 'Personnel & paies', ar: 'الموظفون',        emoji: '👔' },
  { id: 'client_pay',    tables: ['client_payments'],               fr: 'Paiements clients', ar: 'مدفوعات العملاء', emoji: '💰' },
  { id: 'mokhalafat',    tables: ['mokhalafat'],                    fr: 'Contraventions',    ar: 'المخالفات',       emoji: '⚠️' },
  { id: 'expenses',      tables: ['expenses'],                      fr: 'Dépenses',          ar: 'المصاريف',        emoji: '🧾' },
  { id: 'finances',      tables: ['expenses', 'client_payments', 'staff_payments'], fr: 'Finances (dépenses + paiements)', ar: 'المالية (مصاريف + مدفوعات)', emoji: '💵' },
  { id: 'gps_local',     tables: [],  local: true,                  fr: 'Données GPS (local)', ar: 'بيانات GPS (محلي)', emoji: '📍' },
];

export default function ClearDataModal({ onClose, lang = 'fr' }) {
  const isAr = lang === 'ar';
  const [selected, setSelected] = useState({});
  const [slide, setSlide] = useState(0);     // slide-to-confirm (0 → 100)
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const anySelected = Object.values(selected).some(Boolean);
  const allSelected = CATEGORIES.every(c => selected[c.id]);
  const confirmed = slide >= 100;

  const toggle = (id) => { setSelected(s => ({ ...s, [id]: !s[id] })); setSlide(0); };
  const toggleAll = () => {
    const next = !allSelected;
    setSelected(Object.fromEntries(CATEGORIES.map(c => [c.id, next])));
    setSlide(0);
  };

  const handleDelete = async () => {
    if (!anySelected || !confirmed || busy) return;
    const agencyId = window.__AGENCY_ID__;
    if (!agencyId) { alert('Agence introuvable.'); return; }

    setBusy(true);
    const summary = [];
    try {
      for (const cat of CATEGORIES) {
        if (!selected[cat.id]) continue;

        if (cat.local) {
          // Clear local GPS caches (tracks, last positions, speed-limit logs…).
          Object.keys(localStorage)
            .filter(k => /^gps/i.test(k) || k.includes('Track') || k.includes('gpsLastMoved'))
            .forEach(k => localStorage.removeItem(k));
          summary.push(isAr ? cat.ar : cat.fr);
          continue;
        }

        for (const table of cat.tables) {
          const { error } = await supabase.from(table).delete().eq('agency_id', agencyId);
          if (error) throw new Error(`${table}: ${error.message}`);
        }
        summary.push(isAr ? cat.ar : cat.fr);
      }
      setResult({ ok: true, items: summary });
    } catch (e) {
      setResult({ ok: false, error: e.message, items: summary });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center"><Trash2 className="w-5 h-5 text-red-600" /></span>
            <div>
              <h3 className="font-black text-gray-900 dark:text-white text-base">{isAr ? 'مسح البيانات' : 'Effacer les données'}</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">{isAr ? 'اختر ما تريد حذفه' : 'Choisissez ce que vous voulez supprimer'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          /* ── Result screen ── */
          <div className="p-6 text-center">
            {result.ok ? (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">{isAr ? 'تم الحذف' : 'Suppression terminée'}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{(isAr ? 'تم مسح: ' : 'Effacé : ') + result.items.join(' · ')}</p>
              </>
            ) : (
              <>
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">{isAr ? 'حدث خطأ' : 'Erreur'}</h4>
                <p className="text-xs text-red-500 mb-4">{result.error}</p>
              </>
            )}
            <button onClick={onClose} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm">{isAr ? 'إغلاق' : 'Fermer'}</button>
          </div>
        ) : (
          <>
            {/* Checklist */}
            <div className="p-4 overflow-y-auto flex-1 space-y-2">
              <button onClick={toggleAll} className="w-full text-left text-xs font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                {allSelected ? (isAr ? '✕ إلغاء تحديد الكل' : '✕ Tout désélectionner') : (isAr ? '✓ تحديد الكل' : '✓ Tout sélectionner')}
              </button>
              {CATEGORIES.map(cat => (
                <label key={cat.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${selected[cat.id] ? 'border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-800' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  <input type="checkbox" checked={!!selected[cat.id]} onChange={() => toggle(cat.id)} className="w-5 h-5 accent-red-600 cursor-pointer" />
                  <span className="text-lg">{cat.emoji}</span>
                  <span className="font-bold text-sm text-gray-800 dark:text-gray-200">{isAr ? cat.ar : cat.fr}</span>
                </label>
              ))}
            </div>

            {/* Warning + slide to confirm */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-800 dark:text-amber-300 font-medium">
                  {isAr ? 'الحذف نهائي ولا يمكن التراجع عنه. يخص وكالتك فقط.' : 'Suppression définitive et irréversible. Concerne uniquement votre agence.'}
                </p>
              </div>

              <div>
                <div className="flex justify-between text-[11px] font-bold mb-1">
                  <span className={confirmed ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}>
                    {confirmed ? (isAr ? '🔓 جاهز للحذف' : '🔓 Prêt à supprimer') : (isAr ? 'اسحب للتأكيد →' : 'Glissez pour confirmer →')}
                  </span>
                  <span className="text-gray-400">{slide}%</span>
                </div>
                <input
                  type="range" min="0" max="100" value={slide}
                  disabled={!anySelected || busy}
                  onChange={e => setSlide(Number(e.target.value))}
                  className="w-full accent-red-600 cursor-pointer disabled:opacity-40"
                />
              </div>

              <div className="flex gap-2">
                <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 rounded-xl font-bold text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                  {isAr ? 'إلغاء' : 'Annuler'}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!anySelected || !confirmed || busy}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {busy ? (isAr ? 'جارٍ الحذف…' : 'Suppression…') : (isAr ? 'حذف المحدد' : 'Supprimer')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
